/**
 * The defaults a task gets when someone captures it in a hurry.
 *
 * These must match what a person actually gets from Quick Capture, because
 * that is the promise the name makes: the same words through any door produce
 * the same task. The authority is `ROW_DEFAULTS` in the web package's
 * bulkTaskParser — a capture row starts at priority 3, thirty minutes, medium
 * energy, and only the NLP parser moves those.
 *
 * Only `estimated_minutes` differs from `createTask`'s neutral fallbacks
 * (thirty rather than sixty), and that difference is real: `createTask` is the
 * last line of defence for every writer in the app — an import, a sync merge,
 * a restored backup — while quick capture is a person naming one small thing.
 * Priority agrees at 3 on both paths, and should: nothing about typing fast
 * says the work matters more.
 *
 * The values lived inline in QuickCapture.tsx, which meant no other entry
 * point could reach them, and "add a task" through the MCP connector fell
 * through to `createTask` and produced sixty minutes instead of thirty. Nobody
 * decided that; it was two literals in two files drifting apart. Extracting
 * them here also, briefly, shipped priority 4 to the connector — a misreading
 * of a dead `?? 4` fallback in QuickCapture that the row defaults meant was
 * never reached. Read ROW_DEFAULTS, not the fallback, when changing these.
 *
 * Anything that offers quick, low-ceremony capture uses this. Change a number
 * here and every door changes together.
 */
/**
 * `needs_review` is false because quick capture always knows its project —
 * either the user picked one or a `#Header` line named it. A task that landed
 * somewhere on purpose does not need triaging in the inbox.
 */
export const CAPTURE_DEFAULTS = Object.freeze({
    priority: 3,
    difficulty: 3,
    energy_level: 'medium',
    estimated_minutes: 30,
    needs_review: false,
});
/**
 * Fill in whatever the caller did not specify.
 *
 * `null` counts as unspecified alongside `undefined`: a natural-language
 * parser that found no duration returns null, and a tool call that omitted
 * the field returns undefined. Both mean "you decide", and treating them
 * differently would make the parser's misses land as nulls in the database.
 */
export function withCaptureDefaults(fields = {}) {
    return {
        priority: fields.priority ?? CAPTURE_DEFAULTS.priority,
        difficulty: fields.difficulty ?? CAPTURE_DEFAULTS.difficulty,
        energy_level: fields.energy_level ?? CAPTURE_DEFAULTS.energy_level,
        estimated_minutes: fields.estimated_minutes ?? CAPTURE_DEFAULTS.estimated_minutes,
        needs_review: fields.needs_review ?? CAPTURE_DEFAULTS.needs_review,
    };
}
//# sourceMappingURL=capture-defaults.js.map